import React, { useContext, useEffect, useMemo, useState } from 'react';
import { BookWithIllustrations, Language, LanguageContextData } from './types';
import { Page, Text, Image, View, Document as PDFDocument, PDFViewer, usePDF } from '@react-pdf/renderer';
import { textValues, LanguageContext } from './utils';
import { FaDownload } from 'react-icons/fa';

const IMG_STYLE = { width: 300, height: 300 };
const PAGE_SIZE = 'A5';
const COLORS = [ 'white', 'darkgray', 'beige', 'pink', '#f07f73', 'red', 'violet', 'blue', '#73f0ee', 'lightblue', 'lightgreen', '#a1f073', 'green', 'lime', 'yellow', 'lightyellow', '#e87209', '#634a34', 'brown', '#292828', 'black' ];

interface ColorPickerProps {
  setColor: (color: string) => void,
  colors: string[],
  initialColor: string,
};

const ColorPicker = ({ setColor, colors, initialColor }: ColorPickerProps) => {
  const [color, setColorLocal] = useState<string>(initialColor);

  return (
    <div className='colorPicker' style={{ marginBottom: 20 }}>
      { colors.map((c, index) => (
        <span key={ index } className={ 'color' + (color === c ? ' active' : '') } style={{ backgroundColor: c }} onClick={ (e) => { setColorLocal(c); setColor(c); }} />
      )) }
    </div>
  );
};

interface BookRenderingPageProps {
  bookWithIllustrations: BookWithIllustrations
};

/*
 * Title Page
 * Blank Backside (for some nice words)
 * Book Pages (front & backside) - add blank if needed
 * The End
 * Book Backside
*/

const renderBookAsPDF = (bookWithIllustrations: BookWithIllustrations, language: Language, backgroundColor: string, textColor: string) => {
  return (
    <PDFDocument pageLayout='singlePage' pageMode='fullScreen' >
      <Page style={{ backgroundColor }} size={ PAGE_SIZE } orientation='landscape' >
        <View style={{ margin: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 20 }}>
          <Text style={{ color: textColor, fontSize: 24, textAlign: 'center', fontFamily: 'Times-BoldItalic' }}>{ bookWithIllustrations.title }</Text>
          <Image style={ IMG_STYLE } src={ bookWithIllustrations.titleIllustration } />
        </View>
      </Page>
      <Page style={{ backgroundColor }} size={ PAGE_SIZE } orientation='landscape' />
      { bookWithIllustrations.text.map((paragraph, index) => (
        <Page key={ index } style={{ backgroundColor }} size={ PAGE_SIZE } orientation='landscape' >
          <View style={{ margin: 'auto', display: 'flex', flexDirection: (index % 2 === 0) ? 'row' : 'row-reverse', alignItems: 'flex-start', justifyContent: 'flex-start', gap: 10 }}>
            <Text style={{ color: textColor, width: 250, fontSize: 18, fontFamily: 'Times-Italic' }}>{ paragraph }</Text>
            <Image style={ IMG_STYLE } src={ bookWithIllustrations.textIllustrations[index] } />
          </View>
        </Page>
      )) }
      { (bookWithIllustrations.text.length % 2 !== 0) && <Page style={{ backgroundColor }} size={ PAGE_SIZE } orientation='landscape' /> }
      <Page style={{ backgroundColor }} size={ PAGE_SIZE } orientation='landscape' >
        <View style={{ margin: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 20 }}>
          <Image style={ IMG_STYLE } src={ bookWithIllustrations.titleIllustration } />
          <Text style={{ color: textColor, fontSize: 24, textAlign: 'center', fontFamily: 'Times-BoldItalic' }}>{ textValues[language]['end'] }</Text>
        </View>
      </Page>
      <Page style={{ backgroundColor }} size={ PAGE_SIZE } orientation='landscape' >
        <View style={{ margin: 'auto', marginTop: '320px', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'flex-start', gap: 20 }}>
          <Text style={{ color: textColor, fontSize: 24, textAlign: 'center', fontFamily: 'Times-BoldItalic' }}>{ textValues[language]['backside'] }</Text>
        </View>
      </Page>
    </PDFDocument>
  );
};

const BookRenderingPage = ({ bookWithIllustrations }: BookRenderingPageProps) => {
  const { language } = useContext<LanguageContextData>(LanguageContext);
  const [ backgroundColor, setBackgroundColor ] = useState<string>(COLORS[0]);
  const [ textColor, setTextColor ] = useState<string>(COLORS[COLORS.length - 1]);
  const book = useMemo(() => renderBookAsPDF(bookWithIllustrations, language, backgroundColor, textColor), [bookWithIllustrations, language, backgroundColor, textColor])
  const [ pdf, updatePdf ] = usePDF({});

  useEffect(() => updatePdf(book), [book, updatePdf]);

  return (
    <article>
      <h1>{ textValues[language]['rendering'] }</h1>
      <p>{ textValues[language]['renderingText'] }</p>
      <div className='spreadRow'>
        <div>
          <h3>{ textValues[language]['renderingBackgroundColor'] }</h3>
          <ColorPicker colors={ COLORS } setColor={ setBackgroundColor } initialColor={ backgroundColor } />
        </div>
        <div>
          <h3>{ textValues[language]['renderingTextColor'] }</h3>
          <ColorPicker colors={ COLORS } setColor={ setTextColor } initialColor={ textColor } />
        </div>
      </div>
      
      <div className='row' style={{ marginBottom: 20 }}>
        &nbsp;
        { !pdf.loading && pdf.url && <a href={ pdf.url } download='uniquebook.pdf' className='button'>Download <FaDownload /></a> }   
      </div>

      <PDFViewer width='100%' height='550px'>
        { book }
      </PDFViewer>

      <div className='row' style={{ marginTop: 20 }}>
        &nbsp;
        { <a href={ `data:text/json;charset=utf-8,${encodeURIComponent(JSON.stringify(bookWithIllustrations))}` } download='book_data.json' className='button'>Raw Book Data <FaDownload /></a> }   
      </div>
    </article>
  );
};

export default BookRenderingPage;